'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import Cookies from 'js-cookie';
import { toast } from '@/components/ui/use-toast';
import { clearAuthCookies, retrieveCustomerMe } from '@/lib/actions/customer/action';
import { useEffect, useState } from 'react';
import { useHost } from '@/app/[domain]/host-provider';
import { AxiosError } from 'axios';

type AuthCustomerLayoutProps = {
  children: React.ReactNode;
};

export default function AuthCustomerLayout({ children }: AuthCustomerLayoutProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const host = useHost();

  const checkout_url = searchParams.get('checkout_url') || '';
  const isPreview = searchParams.get('isPreview') === 'true';
  const token = Cookies.get('accessToken');

  const [isLoadingPage, setIsLoadingPage] = useState(true);

  const isAccountPage = pathname.startsWith('/account');
  const isAuthPage = pathname === '/account/signin' || pathname === '/account/signup';
  const isProtectedAccountPage = isAccountPage && !isAuthPage;
  const isCheckoutPage = pathname.startsWith('/checkout');

  useEffect(() => {
    if (isPreview) {
      setIsLoadingPage(false);
      return;
    }

    let ignore = false;
    const message = 'Unable to verify your session. Please login again.';

    const verifyToken = async () => {
      if (!token) {
        setIsLoadingPage(false);
        return;
      }

      try {
        const res = await retrieveCustomerMe(host, token);
        if (ignore) return;

        if (res.success) {
          handleVerifySuccess();
        } else {
          handleUnauthenticated(message);
        }
      } catch (error) {
        if (!ignore && error instanceof AxiosError) {
          handleUnauthenticated(message);
        }
      }
    };

    verifyToken();

    return () => {
      ignore = true;
    };
  }, [token, pathname, isPreview]);

  /**
   * Clears authentication cookies and removes the cart cookie.
   * This function is called when the user is unauthenticated or when the token verification fails.
   * It ensures that the user's session is cleared and that any sensitive information is removed.
   * It also removes the cart ID cookie to prevent any potential issues with the shopping cart.
   * @returns {Promise<void>}
   * @throws {Error} If there is an error while clearing the cookies.
   */
  const removeAuthCookies = async (): Promise<void> => {
    await clearAuthCookies();
    await fetch('/api/remove-cart-cookie', { method: 'GET' });
    Cookies.remove('accessToken');
    Cookies.remove('_jstore_cart_id');
  };

  /**
   * Handles the success case of token verification.
   * Sets the loading state to false and redirects to the checkout URL or home page.
   * If the current page is an authentication page, it redirects to the checkout URL.
   * @returns {void}
   */
  const handleVerifySuccess = (): void => {
    setIsLoadingPage(false);

    // Redirect to the checkout URL if available
    if (pathname === '/account/signin' && checkout_url) {
      // If logged in and on signin with checkout_url, redirect to it
      window.location.href = checkout_url;
      return;
    }

    if (isAuthPage) {
      // If already logged in and on signin/signup, redirect to home
      window.location.href = '/';
    }
  };

  /**
   * Handles the unauthenticated case.
   * Clears the authentication cookies and redirects to the sign-in page.
   * If the current page is not an authentication page, it shows an error message.
   * @param {string} [message] - Optional error message to display.
   * @returns {Promise<void>}
   */
  const handleUnauthenticated = async (message?: string): Promise<void> => {
    setIsLoadingPage(false);

    const hasToken = !!token;

    // Jika di halaman checkout dan ada token, langsung redirect ke signin dan clear cookies
    if (isCheckoutPage && hasToken) {
      await removeAuthCookies();
      const url = new URL('/account/signin', window.location.origin);
      if (checkout_url) url.searchParams.set('checkout_url', checkout_url);
      window.location.href = url.toString();
      return;
    }

    // If not /account/* or /checkout, no redirect, just toast
    if (message && hasToken && !isCheckoutPage) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
        duration: 4000,
      });
    }

    await removeAuthCookies();

    // Redirect to the sign-in page or checkout URL
    setTimeout(() => {
      if (!hasToken) return;

      if (isProtectedAccountPage) {
        window.location.href = '/account/signin';
      } else if (isCheckoutPage) {
        const url = new URL('/account/signin', window.location.origin);
        if (checkout_url) url.searchParams.set('checkout_url', checkout_url);
        window.location.href = url.toString();
      }
      // Untuk halaman umum, tidak redirect
    }, 1500);
  };

  if (isPreview) return <>{children}</>;

  return <>{children}</>;
}
